import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { baguettesData, wrapsData, sectionImages } from '../data/menuData';
import { Flame, Leaf, Star, Sparkles } from 'lucide-react';

const BAGUETTE_BG = sectionImages.baguettes;

const BaguettesWrapsSection = () => {
  const { language, t } = useLanguage();

  const renderBadges = (item) => {
    const badges = [];
    if (item.isPopular) badges.push({ icon: <Star className="w-4 h-4 text-amber-400 fill-amber-400" />, key: 'popular' });
    if (item.isSpicy) badges.push({ icon: <Flame className="w-4 h-4 text-orange-500" />, key: 'spicy' });
    if (item.isVegetarian) badges.push({ icon: <Leaf className="w-4 h-4 text-emerald-500" />, key: 'vegetarian' });
    if (item.isVegan) badges.push({ icon: <Leaf className="w-4 h-4 text-emerald-600 fill-emerald-600" />, key: 'vegan' });
    
    if (badges.length === 0) return null;
    return (
      <div className="flex items-center gap-1.5 ml-2">
        {badges.map((b) => <span key={b.key}>{b.icon}</span>)}
      </div>
    );
  };

  return (
    <section 
      id="baguettes" 
      className="relative overflow-hidden"
      data-testid="baguettes-section"
    >
      {/* Premium Section Header */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BAGUETTE_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/50 via-hanse-dark/60 to-hanse-dark/95" />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 mb-4 bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 backdrop-blur-sm px-4 py-2 rounded-full border border-yellow-400/30"
          >
            <Sparkles className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-300 font-semibold text-sm uppercase tracking-wider">
              {language === 'de' ? 'Frisch gebacken' : 'Freshly Baked'}
            </span>
            <Sparkles className="w-5 h-5 text-yellow-400" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
          >
            {t('menu.baguettes.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-lg md:text-xl max-w-2xl"
          >
            {t('menu.baguettes.subtitle')}
          </motion.p>
        </div>
      </div>

      {/* Premium Menu Content */}
      <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-10 md:py-14">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-10">
            
            {/* Baguettes Column */}
            <div>
              <h3 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-1 bg-gradient-to-r from-amber-400 to-amber-400/0 rounded-full"></span>
                Baguettes
              </h3>
              <div className="space-y-3">
                {baguettesData.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: Math.min(index * 0.05, 0.2) }}
                    className="group relative bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-amber-400/30 hover:bg-white/10 transition-all duration-300"
                    data-testid={`baguette-item-${item.id}`}
                  >
                    {item.isPopular && (
                      <div className="absolute -top-2 right-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        <Star className="w-3 h-3 inline mr-1 fill-white" />
                        {language === 'de' ? 'Beliebt' : 'Popular'}
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h4 className="font-bold text-white group-hover:text-amber-300 transition-colors">
                            {language === 'de' ? item.name : item.nameEn}
                          </h4>
                          {renderBadges(item)}
                        </div>
                        <p className="text-white/50 text-sm mt-1">
                          {language === 'de' ? item.description : item.descriptionEn}
                        </p>
                      </div>
                      <span className="font-bold text-amber-400 text-lg">€{item.price.toFixed(2)}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Wraps Column */}
            <div>
              <h3 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-1 bg-gradient-to-r from-emerald-400 to-emerald-400/0 rounded-full"></span>
                Wraps
              </h3>
              <div className="space-y-3">
                {wrapsData.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: Math.min(index * 0.05, 0.2) }}
                    className="group relative bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-emerald-400/30 hover:bg-white/10 transition-all duration-300"
                    data-testid={`wrap-item-${item.id}`}
                  >
                    {item.isPopular && (
                      <div className="absolute -top-2 right-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        <Star className="w-3 h-3 inline mr-1 fill-white" />
                        {language === 'de' ? 'Beliebt' : 'Popular'}
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h4 className="font-bold text-white group-hover:text-emerald-300 transition-colors">
                            {language === 'de' ? item.name : item.nameEn}
                          </h4>
                          {renderBadges(item)}
                        </div>
                        <p className="text-white/50 text-sm mt-1">
                          {language === 'de' ? item.description : item.descriptionEn}
                        </p>
                      </div>
                      <span className="font-bold text-amber-400 text-lg">€{item.price.toFixed(2)}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BaguettesWrapsSection;
